import { useMemo, useCallback } from "react";
import { ColumnDef } from "@tanstack/react-table";
import type { Ambassador } from "@/modules/ambassadors/types";
import {
  Typography,
  Chip,
  CircularProgress,
  useTheme,
  Box,
} from "@mui/material";
import AssignedAgentDisplay from "@/modules/common/components/assigned-agent-display";

export const useAmbassadorColumns = (
  isLoadingLeadsCount: boolean,
  handleViewLeads: (id: string) => void
): ColumnDef<Ambassador & { leadsCount?: number }>[] => {
  const theme = useTheme();

  const chipBaseStyle = useMemo(
    () => ({
      height: "24px",
      paddingLeft: theme.nawy.spacing.xs,
      paddingRight: theme.nawy.spacing.xs,
      justifyContent: "center",
      alignItems: "center",
      gap: theme.nawy.spacing.xxs,
      borderRadius: theme.nawy.radius.md,
    }),
    [theme]
  );

  const acceptedChipStyle = useMemo(
    () => ({
      ...chipBaseStyle,
      background: theme.nawy.colors.semantic.indicator.greenLight,
    }),
    [chipBaseStyle, theme]
  );

  const invitedChipStyle = useMemo(
    () => ({
      ...chipBaseStyle,
      background: theme.nawy.colors.semantic.indicator.greyLight,
    }),
    [chipBaseStyle, theme]
  );

  const renderName = useCallback(
    ({ row }: { row: { original: Ambassador } }) => (
      <Typography variant="body2" fontWeight={500}>
        {row.original.organization?.name || "N/A"}
      </Typography>
    ),
    []
  );

  const renderPhone = useCallback(
    ({ row }: { row: { original: Ambassador } }) => (
      <Typography variant="body2" fontWeight={500}>
        {row.original.phone || "N/A"}
      </Typography>
    ),
    []
  );

  const renderStatus = useCallback(
    ({ row }: { row: { original: Ambassador } }) => {
      const isAccepted = !!row.original.organizationId;
      return (
        <Chip
          label={isAccepted ? "Accepted" : "Invited"}
          sx={isAccepted ? acceptedChipStyle : invitedChipStyle}
        />
      );
    },
    [acceptedChipStyle, invitedChipStyle]
  );
  const renderLeadsCount = useCallback(
    ({ row }: { row: { original: Ambassador } }) => {
      const ambassador = row.original;
      const cognitoId = ambassador.organization?.brokers?.[0]?.cognitoId;

      if (isLoadingLeadsCount && cognitoId) {
        return <CircularProgress size={16} />;
      }

      const hasLeads = (ambassador.leadsCount || 0) > 0;
      const isClickable = !!cognitoId && hasLeads;

      return (
        <Typography
          variant="body2"
          fontWeight={500}
          onClick={() => isClickable && handleViewLeads(cognitoId!)}
          sx={{
            cursor: isClickable ? "pointer" : "default",
            color: isClickable
              ? theme.nawy.colors.primitives.blue[500]
              : theme.nawy.colors.primitives.grey[700],
            textDecoration: isClickable ? "underline" : "none",
            "&:hover": isClickable
              ? { color: theme.nawy.colors.primitives.blue[600] }
              : {},
          }}
        >
          {ambassador.leadsCount || 0}
        </Typography>
      );
    },
    [isLoadingLeadsCount, handleViewLeads, theme]
  );

  const renderInviteeAgent = useCallback(
    ({ row }: { row: { original: Ambassador } }) => {
      const user = row.original.inviter ?? {
        name: row.original.salesAgentDefaultEmail,
      };
      if (!user) {
        return (
          <Typography variant="body2" color="text.secondary">
            N/A
          </Typography>
        );
      }
      return (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <AssignedAgentDisplay user={user} />
        </Box>
      );
    },
    []
  );

  const renderAssignedAgent = useCallback(
    ({ row }: { row: { original: Ambassador } }) => {
      const leadAssignment = row.original.leadAssignment;
      const isAutoAssignment = leadAssignment === "Auto Assignment";

      if (isAutoAssignment) {
        return (
          <Typography variant="body2" fontWeight={500}>
            Auto Assignment
          </Typography>
        );
      }

      const user = row.original.inviterCreator;

      if (!user) {
        return (
          <Typography variant="body2" color="text.secondary">
            N/A
          </Typography>
        );
      }

      return (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <AssignedAgentDisplay user={user} />
        </Box>
      );
    },
    []
  );

  // --- Columns ---
  return useMemo<ColumnDef<Ambassador & { leadsCount?: number }>[]>(
    () => [
      {
        accessorKey: "organization.name",
        header: "Name",
        cell: renderName,
      },
      {
        id: "assignedAgent",
        header: "Assigned agent",
        cell: renderAssignedAgent,
      },
      {
        accessorKey: "phone",
        header: "Phone number",
        cell: renderPhone,
      },
      {
        id: "status",
        header: "Status",
        cell: renderStatus,
      },
      {
        id: "leadsCount",
        header: "Leads referred",
        cell: renderLeadsCount,
      },
      {
        id: "salesAgent",
        header: "Invitee user",
        cell: renderInviteeAgent,
      },
    ],
    [
      renderName,
      renderPhone,
      renderStatus,
      renderLeadsCount,
      renderInviteeAgent,
      renderAssignedAgent,
    ]
  );
};
